import { SERVICE_PROVIDER_API_NAMES } from 'pages/SettingsV2/serviceConnectionConstants';
import { APIClient } from 'services/helpers/api_helper';
import * as url from 'services/helpers/url_helper';
import {
  ServiceConnection,
  ServiceConnectionBank,
  ServiceConnectionFormValues,
  ServiceProviderKey,
} from 'types/settings/serviceConnectionTypes';

const api = new APIClient();

interface BaseApiResponse {
  message: string;
  error_code: number;
}

interface PaymentConnectionApiItem {
  payment_credential_id: number;
  provider: string;
  provider_merchant_id: string;
  key_name: string;
  api_key: string;
  secret_key?: string | null;
  created_at: string | null;
  wallet_channel_id: string | null;
  wallet_channel_name: string | null;
  bank_code: string | null;
  status: string;
}

interface PaymentConnectionListResponse extends BaseApiResponse {
  data: PaymentConnectionApiItem[] | null;
}

interface PaymentConnectionResponse extends BaseApiResponse {
  data: PaymentConnectionApiItem | null;
}

interface PaymentConnectionDeleteResponse extends BaseApiResponse {
  data: { payment_credential_id: number } | null;
}

interface PaymentConnectionDuplicateCheckData {
  is_duplicate: boolean;
  payment_credential_id?: number;
  provider?: string;
  provider_merchant_id?: string;
  status?: string;
}

interface PaymentConnectionDuplicateCheckResponse extends BaseApiResponse {
  data: PaymentConnectionDuplicateCheckData | null;
}

interface BankDirectoryApiItem {
  code: string;
  name_th: string;
  name_en: string;
  asset_path: string;
  asset_url: string;
}

interface BankDirectoryResponse extends BaseApiResponse {
  data: BankDirectoryApiItem[] | null;
}

const unwrapResponse = <T>(response: T | { data: T }): T => {
  if (
    response &&
    typeof response === 'object' &&
    'data' in response &&
    'status' in response
  ) {
    return (response as { data: T }).data;
  }
  return response as T;
};

const assertOk = <T extends BaseApiResponse>(response: T): T => {
  if (response.error_code !== 0) {
    throw new Error(response.message || 'Service connection request failed');
  }
  return response;
};

const normalizeProvider = (provider: string): ServiceProviderKey => {
  const normalized = provider.trim().toLowerCase().replace(/\s+/g, '');
  if (normalized === 'beam') return 'beam';
  // if (normalized === 'qrcode') return 'qrcode';
  return 'beam';
};

const toServiceConnection = (
  item: PaymentConnectionApiItem
): ServiceConnection => ({
  id: item.payment_credential_id,
  provider: normalizeProvider(item.provider),
  merchantId: item.provider_merchant_id,
  keyName: item.key_name,
  apiKey: item.api_key,
  createdAt: item.created_at ?? '',
  bankAccountNumber: item.wallet_channel_id ?? '',
  bankAccountName: item.wallet_channel_name ?? '',
  bankCode: item.bank_code ?? '',
  isActive: item.status === 'active',
});

export const getServiceConnections = async (
  provider?: ServiceProviderKey
): Promise<ServiceConnection[]> => {
  try {
    const providers = provider
      ? [provider]
      : (Object.keys(SERVICE_PROVIDER_API_NAMES) as ServiceProviderKey[]);
    const responses = await Promise.all(
      providers.map((providerKey) =>
        api.get(url.GET_SERVICE_CONNECTIONS, {
          provider: SERVICE_PROVIDER_API_NAMES[providerKey],
        })
      )
    );

    return responses.flatMap(
      (response) =>
        assertOk(
          unwrapResponse<PaymentConnectionListResponse>(response)
        ).data?.map(toServiceConnection) ?? []
    );
  } catch (error) {
    return Promise.reject(error);
  }
};

export const createServiceConnection = async (
  provider: ServiceProviderKey,
  values: ServiceConnectionFormValues
): Promise<ServiceConnection> => {
  try {
    const response = await api.create(url.POST_SERVICE_CONNECTION, {
      provider: SERVICE_PROVIDER_API_NAMES[provider],
      provider_merchant_id: values.merchantId.trim(),
      key_name: values.keyName.trim(),
      api_key: values.apiKey.trim(),
      secret_key: values.secretKey.trim(),
      wallet_channel_id: values.bankAccountNumber.trim() || null,
      wallet_channel_name: values.bankAccountName.trim() || null,
      bank_code: values.bankCode.trim() || null,
    });
    const data = assertOk(
      unwrapResponse<PaymentConnectionResponse>(response)
    ).data;
    if (!data) throw new Error('Missing created service connection data');
    return toServiceConnection(data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const updateServiceConnectionStatus = async (
  id: number,
  isActive: boolean
): Promise<ServiceConnection> => {
  try {
    const response = await api.put(url.PUT_SERVICE_CONNECTION_STATUS(id), {
      status: isActive ? 'active' : 'inactive',
    });
    const data = assertOk(
      unwrapResponse<PaymentConnectionResponse>(response)
    ).data;
    if (!data) throw new Error('Missing updated service connection data');
    return toServiceConnection(data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const deleteServiceConnection = async (id: number): Promise<number> => {
  try {
    const response = await api.delete(url.DELETE_SERVICE_CONNECTION(id));
    assertOk(unwrapResponse<PaymentConnectionDeleteResponse>(response));
    return id;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const checkDuplicateServiceConnection = async (
  provider: ServiceProviderKey,
  merchantId: string
): Promise<boolean> => {
  try {
    const response = await api.get(url.GET_SERVICE_CONNECTION_DUPLICATE, {
      provider: SERVICE_PROVIDER_API_NAMES[provider],
      merchant_id: merchantId.trim(),
    });
    return (
      assertOk(
        unwrapResponse<PaymentConnectionDuplicateCheckResponse>(response)
      ).data?.is_duplicate ?? false
    );
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getServiceConnectionBanks = async (): Promise<
  ServiceConnectionBank[]
> => {
  try {
    const response = await api.get(url.GET_SERVICE_CONNECTION_BANKS);
    return (
      assertOk(unwrapResponse<BankDirectoryResponse>(response)).data?.map(
        (item) => ({
          code: item.code,
          nameTh: item.name_th,
          nameEn: item.name_en,
          assetPath: item.asset_path,
          assetUrl: item.asset_url,
        })
      ) ?? []
    );
  } catch (error) {
    return Promise.reject(error);
  }
};
